import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { UserIdentityProperty } from 'constants/userProperties';
import searchItems from 'utils/searchItems';

import { selectMasterCompany, selectMasterCompanyId } from 'store/auth/selectors';
import { selectFlattenedCompanies } from 'store/companies/selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';

const selectUsers = state => state.users;

const getUserPropertyFlag = (user, propertyName) =>
  user?.userProperties?.userProperties?.find(({ name }) => name === propertyName)?.value === 'true';

export const selectExtendedUsers = createSelector(
  [selectUsers, selectFlattenedCompanies, selectMasterCompany],
  (users, companies, masterCompany) =>
    _.mapValues(users, user => {
      const isUserFormMasterCompany = masterCompany.companyId === user.companyId;
      const companyName = isUserFormMasterCompany
        ? masterCompany.name
        : companies[user.companyId]?.name;

      return {
        ...user,
        email: _.has(user, 'emailAddress') ? user.emailAddress : user.email, // TODO: refactor to use single source of truth after completely switching to new API
        managerUserId: _.has(user, 'managerId') ? user.managerId : user.managerUserId,
        name: [user.firstName, user.middleInitial, user.lastName, user.suffix]
          .filter(Boolean)
          .join(' '),
        companyName: user.companyId ? companyName : '',
        jobTitle: user.jobTitle || '',
        isOwner: getUserPropertyFlag(user, UserIdentityProperty.IS_OWNER),
        isAuthorizedSigner: getUserPropertyFlag(user, UserIdentityProperty.IS_AUTHORIZED_SIGNER),
      };
    }),
);

export const selectExtendedUsersValues = createSelector(selectExtendedUsers, Object.values);

export const selectHasUsersLoaded = createSelector(
  selectUsers,
  users => Object.keys(users ?? {}).length > 0,
);

export const selectUserById = memoizeOne(id =>
  createSelector(selectExtendedUsers, users => users[id]),
);

export const selectIsDomesticUser = memoizeOne(companyId =>
  createSelector(selectMasterCompanyId, masterCompanyId => companyId === masterCompanyId),
);

export const selectUsersOptions = memoizeOne(userId =>
  createSelector(selectExtendedUsersValues, users =>
    users.reduce((acc, user) => {
      const roleNames = user?.roleAssignments?.map(data => data?.role?.name);

      // To exclude himself from list
      if (userId !== user.id) {
        acc.push({
          name: user.name,
          email: user.email,
          value: user.id,
          ...(roleNames && { roleNames }),
        });
      }

      return acc;
    }, []),
  ),
);

export const selectFilteredUsers = createSelector(
  getDebouncedSearchValue,
  selectExtendedUsersValues,
  searchItems(({ firstName, lastName, email, companyName }) => [
    firstName,
    lastName,
    email,
    companyName,
  ]),
);
